/**
 * useUpdateAppJob Hook
 *
 * Custom React Query mutation hook for updating appJobs.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appJobService } from '../../../api/app-job.service';
import type { AppJobUpdateDTO } from '../../../types/app-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UpdateAppJobInput {
  appJobId: string;
  data: AppJobUpdateDTO;
}

interface UseUpdateAppJobOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating a appJob
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useUpdateAppJob = (
  tenantId: string,
  options?: UseUpdateAppJobOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appJobId, data }: UpdateAppJobInput) =>
      appJobService.updateAppJob(tenantId, appJobId, data),
    onSuccess: () => {
      // Invalidate all appJob queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['appJobs', tenantId],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['appJob', tenantId],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
